import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api, type CreativeMemoryItem } from '../../../services/api';

// Memória da marca: o que o Criar Conteúdo aprendeu sobre como esta marca gosta dos conteúdos.
// Tudo aqui entra no prompt dos agentes. O usuário vê, edita, desliga, apaga e escreve à mão.

const CATEGORY_ORDER = ['tom', 'visual', 'formato', 'evitar', 'publicacao', 'outro'];
const FALLBACK_LABELS: Record<string, string> = {
  tom: 'Tom e linguagem', visual: 'Visual', formato: 'Formato', evitar: 'O que evitar', publicacao: 'Publicação', outro: 'Outras preferências',
};
const SOURCE_BADGE: Record<string, { label: string; cls: string; title: string }> = {
  user: { label: 'você disse', cls: 'bg-indigo-50 text-indigo-700', title: 'Você falou isso no chat e a IA anotou.' },
  manual: { label: 'você escreveu', cls: 'bg-slate-100 text-slate-700', title: 'Escrito ou editado por você nesta tela.' },
  inferred: { label: 'a IA percebeu', cls: 'bg-amber-50 text-amber-800', title: 'A IA deduziu pelo que você pediu, recusou ou corrigiu. Confira.' },
};

function MemoryRow({ item, labels, onChange, onDelete }: {
  item: CreativeMemoryItem;
  labels: Record<string, string>;
  onChange: (id: string, patch: { category?: string; content?: string; active?: boolean }) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.content);
  const [category, setCategory] = useState(item.category);
  const [busy, setBusy] = useState(false);
  const badge = SOURCE_BADGE[item.source] || SOURCE_BADGE.manual;
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onChange(item.id, { content: text.trim(), category }); setEditing(false); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${item.active ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50'}`}>
      {editing ? (
        <div className="space-y-2">
          <textarea value={text} onChange={e => setText(e.target.value)} rows={2} maxLength={300} autoFocus
            className="w-full resize-none rounded-lg border border-slate-300 px-2.5 py-2 text-base outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 sm:text-sm" />
          <div className="flex flex-wrap items-center gap-2">
            <select value={category} onChange={e => setCategory(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
              {CATEGORY_ORDER.map(c => <option key={c} value={c}>{labels[c] || c}</option>)}
            </select>
            <button type="button" disabled={busy || !text.trim()} onClick={save} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Salvar</button>
            <button type="button" disabled={busy} onClick={() => { setEditing(false); setText(item.content); setCategory(item.category); }} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancelar</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <label className="mt-0.5 flex shrink-0 cursor-pointer items-center" title={item.active ? 'Ativa: a IA segue esta preferência' : 'Desligada: a IA ignora'}>
            <input type="checkbox" checked={item.active} disabled={busy}
              onChange={async e => { setBusy(true); try { await onChange(item.id, { active: e.target.checked }); } finally { setBusy(false); } }}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
          </label>
          <div className="min-w-0 flex-1">
            <p className={`break-words text-sm ${item.active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{item.content}</p>
            <span title={badge.title} className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>
          </div>
          <div className="flex shrink-0 gap-1">
            <button type="button" onClick={() => setEditing(true)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Editar">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
            </button>
            <button type="button" disabled={busy} onClick={async () => { if (!window.confirm('Apagar esta preferência?')) return; setBusy(true); try { await onDelete(item.id); } finally { setBusy(false); } }}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" aria-label="Apagar">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function BrandMemoryView({ brandId, brandName }: { brandId: string | null; brandName?: string | null }) {
  const [items, setItems] = useState<CreativeMemoryItem[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>(FALLBACK_LABELS);
  const [autoLearn, setAutoLearn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState('tom');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.listCreativeMemory(brandId);
      setItems(res.data.items || []);
      setAutoLearn(res.data.settings?.autoLearn !== false);
      if (res.data.categories) setLabels({ ...FALLBACK_LABELS, ...res.data.categories });
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar a memória');
    } finally {
      setLoading(false);
    }
  }, [brandId]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  const change = async (id: string, patch: { category?: string; content?: string; active?: boolean }) => {
    try {
      const res = await api.updateCreativeMemory(id, patch);
      setItems(prev => prev.map(m => (m.id === id ? res.data : m)));
    } catch (e: any) { toast.error(e?.message || 'Erro ao salvar'); }
  };
  const remove = async (id: string) => {
    try { await api.deleteCreativeMemory(id); setItems(prev => prev.filter(m => m.id !== id)); } catch (e: any) { toast.error(e?.message || 'Erro ao apagar'); }
  };
  const add = async () => {
    if (!newText.trim()) return;
    setAdding(true);
    try {
      const res = await api.createCreativeMemory(brandId, newCategory, newText.trim());
      setItems(prev => [...prev.filter(m => m.id !== res.data.id), res.data]);
      setNewText('');
      toast.success('Preferência salva. Já vale para as próximas peças.');
    } catch (e: any) { toast.error(e?.message || 'Erro ao salvar'); } finally { setAdding(false); }
  };
  const toggleAuto = async (value: boolean) => {
    setAutoLearn(value);
    try { await api.setCreativeMemorySettings(brandId, value); } catch (e: any) { setAutoLearn(!value); toast.error(e?.message || 'Erro ao salvar'); }
  };

  const active = items.filter(m => m.active).length;

  return (
    <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-4 sm:px-6 sm:py-5">
      <div className="mx-auto max-w-3xl space-y-5">
        <div>
          <h2 className="text-lg font-black text-slate-900">Memória da marca{brandName ? ` · ${brandName}` : ''}</h2>
          <p className="mt-1 text-sm text-slate-500">
            O que a IA sabe sobre como você gosta dos conteúdos. Tudo que estiver ligado aqui vale em toda conversa, na Ideação e na Produção.
            Desligue o que não vale mais, edite o que estiver errado ou escreva uma preferência.
          </p>
        </div>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <input type="checkbox" checked={autoLearn} onChange={e => toggleAuto(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
          <span>
            <span className="block text-sm font-semibold text-slate-800">Aprender automaticamente</span>
            <span className="block text-xs text-slate-500">Ao fim de cada conversa, a IA lê o que você pediu, recusou e corrigiu e anota as preferências que percebeu (marcadas como "a IA percebeu"). Quando você diz algo como "nunca faça isso" no chat, ela anota na hora, mesmo com esta opção desligada.</span>
          </span>
        </label>

        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Adicionar preferência</p>
          <textarea value={newText} onChange={e => setNewText(e.target.value)} rows={2} maxLength={300}
            placeholder='Ex.: "Legenda curta, no máximo 3 linhas, sem emoji" ou "Nunca usar fundo branco"'
            className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-base outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 sm:text-sm" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select value={newCategory} onChange={e => setNewCategory(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
              {CATEGORY_ORDER.map(c => <option key={c} value={c}>{labels[c] || c}</option>)}
            </select>
            <button type="button" disabled={adding || !newText.trim()} onClick={add} className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              {adding ? 'Salvando…' : 'Adicionar'}
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400">Carregando…</p>
        ) : items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
            Nada salvo ainda. Conforme você usar o Criar Conteúdo, a IA vai anotando aqui o que você gosta e o que não gosta.
          </p>
        ) : (
          <>
            <p className="text-xs text-slate-500">{active} de {items.length} preferência(s) ligada(s)</p>
            {CATEGORY_ORDER.map(cat => {
              const inCat = items.filter(m => (CATEGORY_ORDER.includes(m.category) ? m.category : 'outro') === cat);
              if (!inCat.length) return null;
              return (
                <section key={cat}>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">{labels[cat] || cat}</p>
                  <div className="space-y-2">
                    {inCat.map(m => <MemoryRow key={m.id} item={m} labels={labels} onChange={change} onDelete={remove} />)}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
